import React, { useState, useEffect } from 'react';
import ToolLayout from '../components/ToolLayout';

interface MatchResult {
  match: string;
  index: number;
  groups: string[];
}

const RegexTester: React.FC = () => {
  const [pattern, setPattern] = useState('');
  const [flags, setFlags] = useState('g');
  const [testString, setTestString] = useState('');
  const [matches, setMatches] = useState<MatchResult[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [highlightedText, setHighlightedText] = useState('');

  useEffect(() => {
    testRegex();
  }, [pattern, flags, testString]);

  const testRegex = () => {
    setError('');
    setSuccess('');
    setMatches([]);
    setHighlightedText(testString);

    if (!pattern.trim()) {
      return;
    }

    try {
      const regex = new RegExp(pattern, flags);
      const results: MatchResult[] = [];
      let highlighted = testString;

      if (flags.includes('g')) {
        // Global search
        let match;
        const offset = 0;
        const originalString = testString;
        
        while ((match = regex.exec(testString)) !== null) {
          results.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1)
          });
          
          // Prevent infinite loop
          if (!match[0]) {break;}
        }

        // Highlight matches
        if (results.length > 0) {
          highlighted = highlightMatches(testString, results);
          setSuccess(`Tìm thấy ${results.length} match(es)! ✅`);
        } else {
          setSuccess('Không tìm thấy matches');
        }
      } else {
        // Single match
        const match = regex.exec(testString);
        if (match) {
          results.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1)
          });
          highlighted = highlightMatches(testString, results);
          setSuccess('Tìm thấy 1 match! ✅');
        } else {
          setSuccess('Không tìm thấy matches');
        }
      }

      setMatches(results);
      setHighlightedText(highlighted);
      
    } catch (err) {
      setError('Regex không hợp lệ: ' + (err as Error).message);
      setMatches([]);
      setHighlightedText(testString);
    }
  };

  const highlightMatches = (text: string, matchResults: MatchResult[]): string => {
    if (matchResults.length === 0) {return text;}

    // Sort matches by index (descending) to avoid index shifting when inserting tags
    const sortedMatches = [...matchResults].sort((a, b) => b.index - a.index);
    let result = text;

    sortedMatches.forEach((match, i) => {
      const start = match.index;
      const end = start + match.match.length;
      const before = result.slice(0, start);
      const matchText = result.slice(start, end);
      const after = result.slice(end);
      
      result = before + `<mark style="background-color: #fbbf24; padding: 2px 4px; border-radius: 3px; font-weight: 600;">${matchText}</mark>` + after;
    });

    return result;
  };

  const clearAll = () => {
    setPattern('');
    setTestString('');
    setMatches([]);
    setError('');
    setSuccess('');
    setHighlightedText('');
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess(`Đã copy ${type} vào clipboard!`);
    } catch (err) {
      setError('Không thể copy vào clipboard');
    }
  };

  const commonPatterns = [
    { name: 'Email', pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}', description: 'Email addresses' },
    { name: 'Phone', pattern: '\\+?[1-9]\\d{1,14}', description: 'Phone numbers' },
    { name: 'URL', pattern: 'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)', description: 'URLs' },
    { name: 'IP Address', pattern: '\\b(?:[0-9]{1,3}\\.){3}[0-9]{1,3}\\b', description: 'IPv4 addresses' },
    { name: 'Date (YYYY-MM-DD)', pattern: '\\d{4}-\\d{2}-\\d{2}', description: 'ISO date format' },
    { name: 'Credit Card', pattern: '\\d{4}[\\s-]?\\d{4}[\\s-]?\\d{4}[\\s-]?\\d{4}', description: 'Credit card numbers' },
    { name: 'Hex Color', pattern: '#[a-fA-F0-9]{6}', description: 'Hex color codes' },
    { name: 'HTML Tag', pattern: '<[^>]+>', description: 'HTML tags' }
  ];

  const flagOptions = [
    { value: 'g', label: 'Global', description: 'Tìm tất cả matches' },
    { value: 'i', label: 'Ignore Case', description: 'Không phân biệt hoa thường' },
    { value: 'm', label: 'Multiline', description: '^$ match đầu/cuối mỗi dòng' },
    { value: 's', label: 'Dot All', description: '. matches newlines' },
    { value: 'u', label: 'Unicode', description: 'Full Unicode support' },
    { value: 'y', label: 'Sticky', description: 'Match chỉ tại lastIndex' }
  ];

  return (
    <ToolLayout 
      title="Regex Tester" 
      description="Test và debug regular expressions với real-time matching"
    >
      {/* Pattern Input */}
      <div className="input-group">
        <label className="input-label">🔍 Regex Pattern:</label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '1.2rem', color: '#666' }}>/</span>
          <input
            type="text"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="Enter your regex pattern..."
            className="regex-pattern-input"
          />
          <span style={{ fontSize: '1.2rem', color: 'var(--text-tertiary)' }}>/</span>
          <input
            type="text"
            value={flags}
            onChange={(e) => setFlags(e.target.value)}
            placeholder="flags"
            className="regex-flags-input"
          />
        </div>
      </div>

      {/* Flags */}
      <div className="input-group">
        <label className="input-label">🚩 Flags:</label>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.5rem',
          padding: '1rem',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          {flagOptions.map((flag) => (
            <label 
              key={flag.value}
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              <input
                type="checkbox"
                checked={flags.includes(flag.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFlags(prev => prev + flag.value);
                  } else {
                    setFlags(prev => prev.replace(flag.value, ''));
                  }
                }}
              />
              <span style={{ fontWeight: '600', color: '#374151' }}>{flag.label}</span>
              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>({flag.description})</span>
            </label>
          ))}
        </div>
      </div>

      {/* Test String */}
      <div className="input-group">
        <label className="input-label">📝 Test String:</label>
        <textarea
          className="input-textarea"
          value={testString}
          onChange={(e) => setTestString(e.target.value)}
          placeholder={'Nhập text để test regex...\n\nVí dụ:\nEmail: john@example.com\nPhone: +1-234-567-8900\nURL: https://example.com'}
          style={{ fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace' }}
          rows={6}
        />
      </div>

      <div className="button-group">
        <button className="action-button" onClick={testRegex}>
          🔍 Test Regex
        </button>
        <button className="action-button secondary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Highlighted Results */}
      {highlightedText && testString && (
        <div className="input-group">
          <label className="input-label">🎯 Highlighted Matches:</label>
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8fafc',
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
            fontSize: '0.9rem',
            lineHeight: '1.6',
            whiteSpace: 'pre-wrap',
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            <div dangerouslySetInnerHTML={{ __html: highlightedText }} />
          </div>
        </div>
      )}

      {/* Match Details */}
      {matches.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ color: '#333', marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>
            📊 Match Details ({matches.length}):
          </h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {matches.map((match, index) => (
              <div 
                key={index}
                style={{
                  padding: '1rem',
                  backgroundColor: '#f0fdf4',
                  border: '2px solid #22c55e',
                  borderRadius: '8px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h4 style={{ margin: 0, color: '#16a34a', fontSize: '1rem' }}>
                    Match #{index + 1}
                  </h4>
                  <button 
                    className="action-button"
                    onClick={() => copyToClipboard(match.match, `Match ${index + 1}`)}
                    style={{ fontSize: '0.75rem', padding: '0.4rem 0.8rem' }}
                  >
                    📋 Copy
                  </button>
                </div>
                <div style={{ fontSize: '0.9rem', color: '#166534' }}>
                  <p style={{ margin: '0 0 0.5rem 0' }}>
                    <strong>Text:</strong> 
                    <code style={{ 
                      marginLeft: '0.5rem', 
                      padding: '0.25rem 0.5rem',
                      backgroundColor: 'rgba(255,255,255,0.7)',
                      borderRadius: '4px',
                      fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace'
                    }}>
                      {match.match}
                    </code>
                  </p>
                  <p style={{ margin: '0 0 0.5rem 0' }}>
                    <strong>Position:</strong> {match.index} - {match.index + match.match.length - 1}
                  </p>
                  {match.groups.length > 0 && (
                    <div>
                      <strong>Capture Groups:</strong>
                      <ul style={{ margin: '0.25rem 0 0 1rem', paddingLeft: 0 }}>
                        {match.groups.map((group, groupIndex) => (
                          <li key={groupIndex} style={{ margin: '0.25rem 0' }}>
                            Group {groupIndex + 1}: 
                            <code style={{ 
                              marginLeft: '0.5rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: 'rgba(255,255,255,0.7)',
                              borderRadius: '4px',
                              fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace'
                            }}>
                              {group || '(empty)'}
                            </code>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Common Patterns */}
      <div style={{ marginTop: '2rem' }}>
        <h3 style={{ color: '#333', marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>
          📚 Common Patterns:
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '0.75rem'
        }}>
          {commonPatterns.map((item, index) => (
            <div 
              key={index}
              onClick={() => setPattern(item.pattern)}
              style={{
                padding: '1rem',
                backgroundColor: '#fafafa',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f9ff';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#fafafa';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <div style={{ fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                {item.name}
              </div>
              <code style={{ 
                fontSize: '0.8rem',
                fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
                color: '#1f2937',
                backgroundColor: 'rgba(255,255,255,0.7)',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                display: 'block',
                marginBottom: '0.5rem',
                wordBreak: 'break-all'
              }}>
                {item.pattern}
              </code>
              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                {item.description}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info Section */}
      <div className="regex-info-section">
        <h4 className="regex-info-title">
          📖 Regex Quick Reference:
        </h4>
        <div className="regex-info-content">
          <div className="regex-info-grid">
            <div>
              <p><code>.</code> - Any character</p>
              <p><code>*</code> - 0 or more</p>
              <p><code>+</code> - 1 or more</p>
              <p><code>?</code> - 0 or 1</p>
            </div>
            <div>
              <p><code>\d</code> - Digit</p>
              <p><code>\w</code> - Word character</p>
              <p><code>\s</code> - Whitespace</p>
              <p><code>^</code> - Start of line</p>
            </div>
            <div>
              <p><code>[abc]</code> - Character set</p>
              <p><code>[^abc]</code> - Negated set</p>
              <p><code>(abc)</code> - Capture group</p>
              <p><code>$</code> - End of line</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .regex-pattern-input,
        .regex-flags-input {
          padding: 0.75rem;
          border: 2px solid var(--border-color);
          border-radius: 6px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          font-size: 0.95rem;
          transition: all 0.3s ease;
        }

        .regex-pattern-input {
          flex: 1;
        }

        .regex-flags-input {
          width: 80px;
        }

        .regex-pattern-input:focus,
        .regex-flags-input:focus {
          outline: none;
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px var(--accent-light);
        }

        .regex-pattern-input::placeholder,
        .regex-flags-input::placeholder {
          color: var(--text-tertiary);
        }

        .regex-info-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 2px solid var(--accent-primary);
        }

        .regex-info-title {
          margin: 0 0 1rem 0;
          color: var(--accent-primary);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
        }

        .regex-info-content {
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .regex-info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .regex-info-grid p {
          margin: 0 0 0.5rem 0;
        }

        .regex-info-grid code {
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          background: var(--card-bg);
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          color: var(--text-primary);
        }
      `}</style>
    </ToolLayout>
  );
};

export default RegexTester;






